import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  selectQuizDataId,
  selectUserQuizId,
  selectUserQuizSlug,
  selectUserQuizType,
} from '@lib/core/quizzes/selectors';
import {
  apiUrlPatchUserQuizAnswer,
  apiUrlPatchUserQuizComplete,
  apiUrlPatchUserQuizMetadata,
  apiUrlPostUserQuiz,
} from '@lib/core/quizzes/slices/urls';
import { TQuizType } from '@lib/core/quizzes/types';
import { TUserQuizDetail } from '@lib/core/quizzes/types/userQuiz';
import { QUIZ_TYPE_EXPERT, QUIZ_TYPE_MULTI_PRODUCT, QUIZ_TYPE_TASTE } from '@lib/core/quizzes/utils/consts';
import { selectRetailerLocationId } from '@lib/core/retailers/selectors/retailerLocation';
import { REQUEST_METHODS, isApplicationPmi } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { uuidv4 } from '@lib/core/service/utils';
import { selectUserSessionId } from '@lib/core/users/selectors/user';
import { selectIsUserKioskAdmin } from '@lib/core/users/selectors/userKiosk';

export interface IUserQuizSlice {
  isFetching: boolean;
  data: TUserQuizDetail;
  error: SerializedError;
}
const initialState: IUserQuizSlice = {
  data: undefined,
  error: null,
  isFetching: false,
};

export const actionPostUserQuiz = createTypedAsyncThunk(
  'quizzes/userQuiz/actionPostUserQuiz',
  async ({ quizSlug, quizType }: { quizSlug: string; quizType: TQuizType }, { getState }) => {
    const retailerLocationId = selectRetailerLocationId(getState());
    const userSessionId = selectUserSessionId(getState());
    const quizTestDataId = selectQuizDataId(getState());
    const userQuizId = selectUserQuizId(getState());
    const quizTypesForUpdateCharacter = [QUIZ_TYPE_MULTI_PRODUCT, QUIZ_TYPE_TASTE, QUIZ_TYPE_EXPERT];

    const shouldSkipCharacterUpdate = !quizTypesForUpdateCharacter.includes(quizType);

    return await request({
      body: {
        quiz_test_data_id: quizTestDataId,
        retailer_location: retailerLocationId,
        test_session_id: userQuizId,
        ...(shouldSkipCharacterUpdate && { is_default: false }),
        ...(!isApplicationPmi && { user_session_id: userSessionId }),
      },
      method: REQUEST_METHODS.POST,
      url: apiUrlPostUserQuiz(quizSlug),
    });
  },
);

/**
 * Submit answer(s) on the `userQuiz`
 */
export const actionPatchUserQuizSubmitAnswer = createTypedAsyncThunk(
  'quizzes/userQuiz/actionPatchUserQuizSubmitAnswer',
  async ({ answerIds, questionId }: { answerIds: string[]; questionId: string }, { getState }) => {
    const userQuizId = selectUserQuizId(getState());
    const userQuizSlug = selectUserQuizSlug(getState());

    const body = {
      answers: answerIds,
      question: questionId,
    };
    return await request({
      body,
      method: REQUEST_METHODS.PATCH,
      url: apiUrlPatchUserQuizAnswer(userQuizSlug, userQuizId),
    });
  },
);

export const actionPatchUserQuizComplete = createTypedAsyncThunk<
  TUserQuizDetail & { itWillUpdateUserCharacter?: boolean },
  { userQuizType?: TQuizType }
>('quizzes/userQuiz/actionPatchUserQuizComplete', async ({ userQuizType }, { getState }) => {
  const retailerLocationId = selectRetailerLocationId(getState());
  const userQuizSlug = selectUserQuizSlug(getState());
  const userQuizId = selectUserQuizId(getState());
  const userSessionId = selectUserSessionId(getState());
  const { isUserQuizTypeTaste, isUserQuizTypeExpert } = selectUserQuizType(getState());

  const isUserKioskAdmin = selectIsUserKioskAdmin(getState());

  const isSetDefault = !isUserKioskAdmin && (isUserQuizTypeTaste || isUserQuizTypeExpert);

  const quizTypesForUpdateCharacter = [QUIZ_TYPE_MULTI_PRODUCT, QUIZ_TYPE_TASTE, QUIZ_TYPE_EXPERT];

  const shouldSkipCharacterUpdate = !quizTypesForUpdateCharacter.includes(userQuizType);

  const body = {
    is_complete: true,
    is_default: !shouldSkipCharacterUpdate,
    retailer_location: retailerLocationId,
    set_default_test: isSetDefault,
    test_session_id: userQuizId,
    user_session_id: undefined,
  };

  if (!isApplicationPmi) {
    body.user_session_id = userSessionId;
  }

  const response = await request({
    body,
    method: REQUEST_METHODS.PATCH,
    url: apiUrlPatchUserQuizComplete(userQuizSlug, userQuizId),
  });
  return !shouldSkipCharacterUpdate ? { ...response, itWillUpdateUserCharacter: true } : response;
});

/**
 * Tracking data on metadata field
 */
export const actionPatchUserQuizMetadata = createTypedAsyncThunk(
  'quizzes/userQuiz/actionPatchUserQuizMetadata',
  async (body: Record<string, any>, { getState }): Promise<any> => {
    const userQuizId = selectUserQuizId(getState());
    const userQuizSlug = selectUserQuizSlug(getState());

    return await request({
      body,
      method: REQUEST_METHODS.PATCH,
      url: apiUrlPatchUserQuizMetadata(userQuizSlug, userQuizId),
    });
  },
);

const userQuizSlice = createSlice({
  extraReducers: builder => {
    builder.addMatcher(
      isAnyOf(actionPostUserQuiz.fulfilled, actionPatchUserQuizComplete.fulfilled),
      (state, action) => {
        state.data = action.payload;
        state.error = null;
        state.isFetching = false;
      },
    );
    builder.addMatcher(
      isAnyOf(actionPostUserQuiz.pending, actionPatchUserQuizSubmitAnswer.pending, actionPatchUserQuizComplete.pending),
      state => {
        state.error = null;
        state.isFetching = true;
      },
    );
    builder.addMatcher(isAnyOf(actionPatchUserQuizSubmitAnswer.fulfilled), state => {
      state.error = null;
      state.isFetching = false;
    });
    builder.addMatcher(isAnyOf(actionPatchUserQuizMetadata.fulfilled), (state, action) => {
      if (state.data) {
        state.data.metadata = action.payload.metadata;
      }
    });
    builder.addMatcher(
      isAnyOf(
        actionPostUserQuiz.rejected,
        actionPatchUserQuizSubmitAnswer.rejected,
        actionPatchUserQuizComplete.rejected,
      ),
      (state, action) => {
        state.isFetching = false;
        state.error = action.error;
      },
    );
  },
  initialState,
  name: 'userQuiz',
  reducers: {
    actionGenerateUserQuizId: state => {
      return {
        ...state,
        data: {
          ...state.data,
          test_session_id: uuidv4(),
        },
      };
    },
    actionResetUserQuiz: () => initialState,
  },
});

export default userQuizSlice.reducer;

export const { actionGenerateUserQuizId, actionResetUserQuiz } = userQuizSlice.actions;
